import ModalComponent from '@/common/Modal';
import { Box, Button, FormControl, FormLabel, Image, Spinner, Text } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react'
import { useDropzone } from "react-dropzone";
import { getUserEmail } from '@/redux/app-selectors';
import Steppers from './Steppers';
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from 'react-redux';
import { callToGetOCRStepFunctionDetailsV2, callToManualReviewRequest, certificateWorkflowInitialState, setCallOcrStepFunctionApi, setIsManualReviewRequested, setIsOCRLoading, setOcrStepFunctionStatusV2 } from '../ducks/slice';
import { getIsManualReviewRequested, getIsOCRLoading, getOcrStepFunctionStatus } from '../ducks/selectors';
import DocumentValidationError from './document-validation-error';
import DocumentValidationInstruction from './document-validation-instruction';
import { getFormattedAddress, hasAddressChanged, hasNameChanged } from '@/utils/utils';
import { getDateTimeForAddresses, getFormattedDate } from '@/queue/elements/value-formatters/date-formatter.mjs';
import CustomCheckbox from '@/utils/customCheckbox';
import { uploadData } from 'aws-amplify/storage';

export interface uploadLicenseModalPropsInterface {
    data: any;
    isOpen: boolean;
    onClose: () => void;
}
// Reusable AddressChangeSection component
interface AddressChangeSectionProps {
    data: any;
    setIsAddressChangedConfirmed: (confirmation: boolean) => void;
    isPremiseAddressChanged: boolean; 
    isMailingAddressChanged: boolean;
}

const AddressChangeSection = ({ data, setIsAddressChangedConfirmed, isPremiseAddressChanged, isMailingAddressChanged}: AddressChangeSectionProps) => {
    const [checked, setChecked] = useState(false);
    
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
        setIsAddressChangedConfirmed(event.target.checked);
    };

    return (
        <Box className="address-change-declaration-section">
            { (isPremiseAddressChanged || isMailingAddressChanged) && <>
            {/* Acknowledgment Section */}
            <Box className="address-change-acknowledgment">
                <input 
                    type="checkbox" 
                    id="address-acknowledgment" 
                    className="address-change-checkbox-input"
                    onChange={handleCheckboxChange}
                    style={{ display: 'none' }} // Hide the native input
                />
                <label htmlFor="address-acknowledgment" style={{ cursor: 'pointer' }}>
                    <CustomCheckbox isChecked={checked} />
                </label>
                <label htmlFor="address-acknowledgment" className="address-change-acknowledgment-text">
                    We have detected a address change from ATF, Please verify address change before uploading.
                </label>
            </Box>
            </>}

            <Box className="address-change-grid">
                {/* Premise Address Section */}
                { isPremiseAddressChanged && <>
                    <Box className="address-change-item">
                        <Box className="address-change-header">Previous Premise Address:</Box>
                        <Box className="address-change-data">
                        {getFormattedAddress(data?.oldPremiseAddress) ?? '-'}
                        </Box>
                    </Box>
                    
                    <Box className="address-change-item">
                        <Box className="address-change-header">Current Premise Address:</Box>
                        <Box className="address-change-data">
                        {getFormattedAddress(data?.premiseAddress) ?? '-'}
                        </Box>
                    </Box>
                    
                    <Box className="address-change-item">
                        <Box className="address-change-header">Changed on:</Box>
                        <Box className="address-change-date">
                        {getDateTimeForAddresses(data?.premiseAddressChangedDate?.toString()) ?? 'Unchanged'}
                        </Box>
                    </Box>
                </>}

                {isMailingAddressChanged && 
                <>
                    {/* Mailing Address Section */}
                    <Box className="address-change-item">
                        <Box className="address-change-header">Previous Mailing Address:</Box>
                        <Box className="address-change-data">
                        {getFormattedAddress(data?.oldMailingAddress) ?? '-'}
                        </Box>
                    </Box>
                    
                    <Box className="address-change-item">
                        <Box className="address-change-header">Current Mailing Address:</Box>
                        <Box className="address-change-data">
                        {getFormattedAddress(data?.mailingAddress) ?? '-'}
                        </Box>
                    </Box>
                    
                    <Box className="address-change-item">
                        <Box className="address-change-header">Changed on:</Box>
                        <Box className="address-change-date">
                        {getDateTimeForAddresses(data?.mailingAddressChangedDate?.toString()) ?? 'Unchanged'}
                        </Box>
                    </Box>
                </>}
            </Box>
        </Box>
    );
};

// Name change section (same pattern as address: previous/current + changed on date)
interface NameChangeSectionProps {
    data: any;
    setIsNameChangedConfirmed: (confirmation: boolean) => void;
    isLicenseNameChanged: boolean;
    isBusinessNameChanged: boolean;
}

const NameChangeSection = ({ data, setIsNameChangedConfirmed, isLicenseNameChanged, isBusinessNameChanged }: NameChangeSectionProps) => {
    const [checked, setChecked] = useState(false);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
        setIsNameChangedConfirmed(event.target.checked);
    };

    return (
        <Box className="address-change-declaration-section">
            {(isLicenseNameChanged || isBusinessNameChanged) && (
                <>
                    <Box className="address-change-acknowledgment">
                        <input
                            type="checkbox"
                            id="name-acknowledgment"
                            className="address-change-checkbox-input"
                            onChange={handleCheckboxChange}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="name-acknowledgment" style={{ cursor: 'pointer' }}>
                            <CustomCheckbox isChecked={checked} />
                        </label>
                        <label htmlFor="name-acknowledgment" className="address-change-acknowledgment-text">
                            We have detected a license or business name change from ATF. Please verify the name change before uploading.
                        </label>
                    </Box>
                </>
            )}

            <Box className="address-change-grid">
                {isLicenseNameChanged && (
                    <>
                        <Box className="address-change-item">
                            <Box className="address-change-header">Previous License Name:</Box>
                            <Box className="address-change-data">{data?.oldLicenseName ?? '-'}</Box>
                        </Box>
                        <Box className="address-change-item">
                            <Box className="address-change-header">Current License Name:</Box>
                            <Box className="address-change-data">{data?.licenseName ?? '-'}</Box>
                        </Box>
                        <Box className="address-change-item">
                            <Box className="address-change-header">Changed on:</Box>
                            <Box className="address-change-date">
                                {getDateTimeForAddresses(data?.licenseNameChangedDate?.toString()) ?? 'Unchanged'}
                            </Box>
                        </Box>
                    </>
                )}
                {isBusinessNameChanged && (
                    <>
                        <Box className="address-change-item">
                            <Box className="address-change-header">Previous Business Name:</Box>
                            <Box className="address-change-data">{data?.oldBusinessName ?? '-'}</Box>
                        </Box>
                        <Box className="address-change-item">
                            <Box className="address-change-header">Current Business Name:</Box>
                            <Box className="address-change-data">{data?.businessName ?? '-'}</Box>
                        </Box>
                        <Box className="address-change-item">
                            <Box className="address-change-header">Changed on:</Box>
                            <Box className="address-change-date">
                                {getDateTimeForAddresses(data?.businessNameChangedDate?.toString()) ?? 'Unchanged'}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

// Reusable FileUploadSection component
interface FileUploadSectionProps {
    openFileDialog: () => void;
    getRootProps: () => any;
    getInputProps: () => any;
    fileRejectionItems: React.ReactNode[];
    files: any[];
}

const FileUploadSection = ({ openFileDialog, getRootProps, getInputProps, fileRejectionItems, files }: FileUploadSectionProps) => {
    const isFileSizeExceeded = files && files.length > 0 && files[0].size / (1024 * 1024) > 8;

    return (
        <Box className="upload_file_wrapper" onClick={openFileDialog}>
            <Image className="upload_file_icon" src="/images/ui/upload.svg" alt="upload-icon" />
            <Box className="upload_file_description_text" {...getRootProps()}>
                <FormControl>
                    <FormLabel style={{ textAlign: 'center', width: '60%', margin: 'auto' }}>
                        Click here to upload a file (File format- jpeg/png/pdf)
                    </FormLabel>
                    <input type='file' {...getInputProps()} style={{ display: 'none' }} />
                    {fileRejectionItems.length > 0 && (
                        <Text style={{ color: 'red', fontWeight: 'bold' }}>
                            Please select file from one of the above types.
                        </Text>
                    )}
                    {isFileSizeExceeded && (
                        <Text style={{ color: 'red', fontWeight: '500', marginTop: '4px' }}>
                            *Please upload file with size less than 8MB*
                        </Text>
                    )}
                </FormControl>
            </Box>
        </Box>
    );
};

// Main LicenseForm component
export interface LicenseFormProps {
    files: any;
    onFileChange: (file: any) => void;
    isFileUploaded: boolean;
    setErrorStepIndex: (index: number) => void;
    setSuccessStepIndex: (index: number) => void;
    setActiveStepIndex: (index: number) => void;
    isPremiseAddressChanged: boolean;
    isMailingAddressChanged: boolean;
    isLicenseNameChanged: boolean;
    isBusinessNameChanged: boolean;
    data: any;
    setIsAddressChangedConfirmed: (confirmation: boolean) => void;
    setIsNameChangedConfirmed: (confirmation: boolean) => void;
}

const hasAnyChange = (isPremise: boolean, isMailing: boolean, isLicenseName: boolean, isBusinessName: boolean) =>
    isPremise || isMailing || isLicenseName || isBusinessName;

export function LicenseForm({
    files,
    onFileChange,
    isFileUploaded,
    setErrorStepIndex,
    setActiveStepIndex,
    setSuccessStepIndex,
    isPremiseAddressChanged,
    isMailingAddressChanged,
    isLicenseNameChanged,
    isBusinessNameChanged,
    data,
    setIsAddressChangedConfirmed,
    setIsNameChangedConfirmed
}: LicenseFormProps) {
    const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
        accept: {
            "image/jpeg": [],
            "image/png": [],
            "image/jpg": [],
            "application/pdf": [],
        },
        noClick: true,
        noKeyboard: true,
        onDrop: (acceptedFiles: any) => {
            if (acceptedFiles.length) {
                onFileChange(acceptedFiles.map((file: any) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    })
                ));
                setErrorStepIndex(0);
                setSuccessStepIndex(1);
                setActiveStepIndex(2);
            }
        },
        multiple: false,
    });

    // Error for rejecting the file and compute the rejected file size
    const fileRejectionItems = fileRejections.map(({ file, errors }: any) => (
        <li key={file.path}>
            {file.path} - {file.size} bytes
            <ul>
                {errors.map((e: any) => (
                    <li key={e.code}>{e.message}</li>
                ))}
            </ul>
        </li>
    ));

    // Preview the file uploaded
    const thumbs = files.map((file: any) => (
        <div className="clear-both" key={file.name}>
            {file.type === "application/pdf" ? (
                <iframe
                    width="100%"
                    className="docs-iframe"
                    id="file-preview-iframe"
                    src={file.preview}
                />
            ) : (
                <Image
                    src={file.preview}
                    alt="Preview"
                    style={{ width: '100%', height: 'auto' }}
                    className="image-preview"
                />
            )}
        </div>
    ));

    // Check if file is uploaded and valid
    const hasValidFile = files && files.length > 0 && files[0].size / (1024 * 1024) <= 8;

    const renderContent = () => {
        if (isFileUploaded) {
            return (
                <Box className="upload_file_progress_wrapper">
                    <Spinner
                        thickness={'4px'}
                        speed="0.65s"
                        emptyColor={'gray.200'}
                        color="#0C1E46"
                        size={'xl'}
                    />
                    <Text>Please wait while the file is uploading</Text>
                </Box>
            );
        }

        if (hasValidFile) {
            return (
                <Box className={hasAnyChange(isPremiseAddressChanged, isMailingAddressChanged, isLicenseNameChanged, isBusinessNameChanged) ? 'address-change-wrapper' : ''}>
                    <Box className="file-preview-modal" sx={{ width: '100%' }}>
                        <Box>{thumbs}</Box>
                    </Box>
                    {(isPremiseAddressChanged || isMailingAddressChanged) && (
                        <AddressChangeSection
                            data={data}
                            setIsAddressChangedConfirmed={setIsAddressChangedConfirmed}
                            isPremiseAddressChanged={isPremiseAddressChanged}
                            isMailingAddressChanged={isMailingAddressChanged}
                        />
                    )}
                    {(isLicenseNameChanged || isBusinessNameChanged) && (
                        <NameChangeSection
                            data={data}
                            setIsNameChangedConfirmed={setIsNameChangedConfirmed}
                            isLicenseNameChanged={isLicenseNameChanged}
                            isBusinessNameChanged={isBusinessNameChanged}
                        />
                    )}
                </Box>
            );
        }

        return (
            <Box className={hasAnyChange(isPremiseAddressChanged, isMailingAddressChanged, isLicenseNameChanged, isBusinessNameChanged) ? 'address-change-wrapper' : 'address-change-upload_file_wrapper'}>
                <FileUploadSection
                    openFileDialog={openFileDialog}
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    fileRejectionItems={fileRejectionItems}
                    files={files}
                />
                {(isPremiseAddressChanged || isMailingAddressChanged) && (
                    <AddressChangeSection
                        data={data}
                        setIsAddressChangedConfirmed={setIsAddressChangedConfirmed}
                        isPremiseAddressChanged={isPremiseAddressChanged}
                        isMailingAddressChanged={isMailingAddressChanged}
                    />
                )}
                {(isLicenseNameChanged || isBusinessNameChanged) && (
                    <NameChangeSection
                        data={data}
                        setIsNameChangedConfirmed={setIsNameChangedConfirmed}
                        isLicenseNameChanged={isLicenseNameChanged}
                        isBusinessNameChanged={isBusinessNameChanged}
                    />
                )}
            </Box>
        );
    };

    return (
        <Box className="sot_form_wrapper" sx={{ display: 'flex', flexDirection: 'column', flexGrow: '1', overflow: 'auto' }}>
            <Box className="sot_form_label">Add License</Box>
            <Box className="upload_file_container" sx={{ overflow: 'auto' }}>
                {renderContent()}
            </Box>
        </Box>
    );
}

const UploadLicenseModal = ({
    data,
    isOpen,
    onClose,
}: uploadLicenseModalPropsInterface) => {
    const [files, setFiles] = useState<any>([]);
    const [isFileUploaded, setIsFileUploaded] = useState(false)
    const [isFileUploadedSuccess, setIsFileUploadedSuccess] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const userEmail = getUserEmail()

    const [activeStepIndex, setActiveStepIndex] = useState(1);
    const [successStepIndex, setSuccessStepIndex] = useState(0);
    const [errorStepIndex, setErrorStepIndex] = useState(0);
    const [isDocumentValidationError, setIsDocumentValidationError] = useState(false)
    const [isReviewPending, setIsReviewPending] = useState(false)
    const ocrStepFunctionStatus: any = getOcrStepFunctionStatus();
    const isOcrLoading = getIsOCRLoading();
    const dispatch = useDispatch();
    const [isAddressChangedConfirmed, setIsAddressChangedConfirmed] = useState(false);
    const [isNameChangedConfirmed, setIsNameChangedConfirmed] = useState(false);
    const [isPremiseAddressChanged, setIsPremiseAddressChanged] = useState(false);
    const [isMailingAddressChanged, setIsMailingAddressChanged] = useState(false);
    const [isLicenseNameChanged, setIsLicenseNameChanged] = useState(false);
    const [isBusinessNameChanged, setIsBusinessNameChanged] = useState(false);
    const isManualReviewRequested = getIsManualReviewRequested();
    const [documentId, setDocumentId] = useState('');

    useEffect(() => {
        setDocumentId('')
        dispatch(setOcrStepFunctionStatusV2(certificateWorkflowInitialState));
        setIsFileUploaded(false)
        setFiles([])
        setIsLoading(false)
        setIsFileUploadedSuccess(false)
        setActiveStepIndex(1)
        setSuccessStepIndex(0)
        setErrorStepIndex(0)
        setIsReviewPending(false);
        setIsDocumentValidationError(false)
        console.log("LIC : ", data);
        const { isPremiseAddressChanged: premiseChanged, isMailingAddressChanged: mailingChanged, hasAnyAddressChanged } = hasAddressChanged(data);
        const { isLicenseNameChanged: licenseNameChanged, isBusinessNameChanged: businessNameChanged, hasAnyNameChanged } = hasNameChanged(data);
        console.log("hasAnyAddressChanged : ", hasAnyAddressChanged, "hasAnyNameChanged : ", hasAnyNameChanged);
        setIsPremiseAddressChanged(premiseChanged);
        setIsMailingAddressChanged(mailingChanged);
        setIsLicenseNameChanged(licenseNameChanged);
        setIsBusinessNameChanged(businessNameChanged);
        dispatch(setIsManualReviewRequested(false));
    }, [isOpen])

    useEffect(() => {
    if (isManualReviewRequested) {
      setErrorStepIndex(0);
      setIsReviewPending(true)
      setIsLoading(false)
      setIsDocumentValidationError(false)
    }
  }, [isManualReviewRequested]);
    useEffect(() => {
        if (ocrStepFunctionStatus?.status == null) {
            setIsLoading(false)
            setIsFileUploadedSuccess(false)
            setActiveStepIndex(1)
            setSuccessStepIndex(0)
            setErrorStepIndex(0)
            setIsReviewPending(false);
            setIsDocumentValidationError(false)
            setDocumentId('')
            return
        }
        if (ocrStepFunctionStatus?.status !== 0 && ocrStepFunctionStatus?.status !== null) {
            dispatch(setCallOcrStepFunctionApi(false));
            if (ocrStepFunctionStatus.status === 13 ||
                ocrStepFunctionStatus.status === 54 ||
                ocrStepFunctionStatus.status === 54 ||
                ocrStepFunctionStatus.status === 14 ||
                ocrStepFunctionStatus.status === 33 ||
                ocrStepFunctionStatus.status === 16 ||
                ocrStepFunctionStatus.status === 15 ||
                ocrStepFunctionStatus.status === 51 ||
                ocrStepFunctionStatus.status === 52
            ) {
                setIsDocumentValidationError(true);
                setSuccessStepIndex(2);
                setErrorStepIndex(3);
                // dispatch(setOcrStepFunctionStatusV2(certificateWorkflowInitialState));
            } else {
                setSuccessStepIndex(2);
                setActiveStepIndex(3)
                setIsReviewPending(true)
                setIsDocumentValidationError(false);
                setIsLoading(false)
            }
        } else if (ocrStepFunctionStatus) {
            if (ocrStepFunctionStatus.status === 0) {
                dispatch(setCallOcrStepFunctionApi(false))
                setSuccessStepIndex(3)
                setIsLoading(false)
                setIsFileUploaded(false)
                setIsFileUploadedSuccess(true)
                setDocumentId('')
            }
        }
    }, [ocrStepFunctionStatus]);

    const onCloseHandler = () => {
        onClose()
        setIsFileUploaded(false)
        setFiles([])
        setIsLoading(false)
        setIsFileUploadedSuccess(false)
        setActiveStepIndex(1)
        setSuccessStepIndex(0)
        setErrorStepIndex(0)
        setIsDocumentValidationError(false)
        setIsAddressChangedConfirmed(false)
        setIsNameChangedConfirmed(false)
        setDocumentId('')
        setTimeout(() => {
            setIsReviewPending(false);
            dispatch(setIsManualReviewRequested(false));
            dispatch(setOcrStepFunctionStatusV2(certificateWorkflowInitialState));
        }, 200);
    }

    const getContentType = (file: any) => {
        switch (file.type) {
            case 'image/png':
                return 'image/png';
            case 'image/jpeg':
                return 'image/jpeg';
            case 'application/pdf':
                return 'application/pdf';
        }
    };
    // create an S3 bucket, upload file and track uploading progress
    const uploadLicenseInS3 = async (files: any) => {
        try {
            if (files) {
                setSuccessStepIndex(2);
                setActiveStepIndex(3);
                const document_id: any = uuidv4();
                const contentType = getContentType(files[0].type);
                // const res = await Storage.put(`${document_id}/${files[0].path}`, files[0], {
                //     contentType: contentType,
                //     metadata: { 
                //         document_id, 
                //         source: "ffl360", 
                //         document_type: 'ffl', 
                //         email: userEmail, 
                //         license_key: data?.fullLicenseKey || "" },
                //         customPrefix: { public: `${data.licenseKey}/` },
                // });
                const res = await uploadData({
                  path: `${data.licenseKey}/${document_id}/${files[0].name}`,
                  data: files[0],
                  options: {
                    contentType: contentType,
                    metadata: {
                      source: "ffl360",
                      documentType: "ffl",
                      document_type: 'ffl',
                      email: userEmail,
                      document_id: document_id,
                      license_key: data?.fullLicenseKey || "",
                    },
                    bucket: "licenseReader",
                  }
                }).result;
                if (res.path) {
                    dispatch(setIsOCRLoading(true));
                    dispatch(setCallOcrStepFunctionApi(true));
                    dispatch(callToGetOCRStepFunctionDetailsV2({ clientDocumentId: document_id }));
                    setDocumentId(document_id)
                }
            }
        } catch (error) {
            console.log({ error });
            onClose();
            setIsFileUploadedSuccess(false)
            setDocumentId('')
        }
    }

    const onUploadNewFile = () => {
        dispatch(setOcrStepFunctionStatusV2(certificateWorkflowInitialState));
        setIsFileUploaded(false)
        setFiles([])
        setIsLoading(false)
        setIsFileUploadedSuccess(false)
        setActiveStepIndex(1)
        setSuccessStepIndex(0)
        setErrorStepIndex(0)
        setIsReviewPending(false);
        setIsDocumentValidationError(false)
        setDocumentId('')
        dispatch(setIsManualReviewRequested(false));
    }

    const onMannualRequest = () => {
      dispatch(setIsManualReviewRequested(false));
      dispatch(callToManualReviewRequest({ clientDocumentId: documentId }));
    }

    return (
        <ModalComponent
            title={(isPremiseAddressChanged || isMailingAddressChanged || isLicenseNameChanged || isBusinessNameChanged) ? 'Please Confirm Address and Name Change' : 'License'}
            isOpen={isOpen}
            onClose={onCloseHandler}
            variant={2}
            className="sot_form_wrapper upload-license-modal"
            footer={
                isOcrLoading || isLoading || isDocumentValidationError ? <></> :
                    (
                        <>{
                            (!isFileUploadedSuccess && !isReviewPending) ?
                                <>
                                {/* {isAddressChanged ?
                                    <Button
                                        colorScheme="blue"
                                        background="#0C1E46"
                                        padding="8px 20px"
                                        marginRight="8px"
                                        onClick={onCloseHandler}
                                        isDisabled={isLoading || isFileUploadedSuccess || files.length === 0 || (files[0]?.size / (1024 * 1024) > 8)}
                                    >
                                        Decline
                                    </Button> : "" } */}
                                    <Button
                                        colorScheme="blue"
                                        background="#0C1E46"
                                        padding="8px 20px"
                                        onClick={() => {
                                            uploadLicenseInS3(files)
                                            setIsFileUploaded(true)
                                            setIsLoading(true)
                                            setIsAddressChangedConfirmed(false)
                                            setIsNameChangedConfirmed(false)
                                        }}
                                        isDisabled={(files.length && (((isPremiseAddressChanged || isMailingAddressChanged) && !isAddressChangedConfirmed) || ((isLicenseNameChanged || isBusinessNameChanged) && !isNameChangedConfirmed))) || isLoading || isFileUploadedSuccess || files.length === 0 || (files[0]?.size / (1024 * 1024) > 8)}
                                    >
                                        Upload
                                    </Button>
                                </>
                                :
                                <Button
                                    colorScheme="blue"
                                    background="#0C1E46"
                                    padding="8px 20px"
                                    onClick={onCloseHandler}
                                >
                                    Done
                                </Button>
                        }
                        </>
                    )
            }
        >
            <Box className='Upload-doc-wrapper'>
                <Box className="step_header">
                    <Box className="stepperMain">
                        <Steppers
                            successSteps={successStepIndex}
                            activeIndex={activeStepIndex}
                            errorIndex={errorStepIndex}
                        />
                    </Box>
                </Box>
                {isFileUploadedSuccess ? (
                    <Box className='file_upload_successful'>
                        <Image src="/images/ui/success-icon.svg" alt="success-icon" />
                        <Text className='file_upload_success_message'>File is successfully uploaded</Text>
                        <Box className='file_upload_success_description'>
                            <Text>It will be reviewed first before being added to your profile.</Text>
                            <Text>Please check your profile after 15 minutes, if license information is not updated kindly contact at support@masterffl.com.</Text>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {isDocumentValidationError ? (
                            <DocumentValidationError documentTypeLabel="FFL License" OnUploadNewFile={onUploadNewFile} onMannualRequest={() => onMannualRequest()} />
                        ) : (
                            <>
                                {isFileUploaded ? (
                                    <DocumentValidationInstruction isReviewPending={isReviewPending} isManualReviewRequested={isManualReviewRequested} />
                                ) : (
                                    <LicenseForm
                                        files={files}
                                        onFileChange={setFiles}
                                        isFileUploaded={isFileUploaded}
                                        setErrorStepIndex={setErrorStepIndex}
                                        setSuccessStepIndex={setSuccessStepIndex}
                                        setActiveStepIndex={setActiveStepIndex}
                                        isPremiseAddressChanged={isPremiseAddressChanged}
                                        isMailingAddressChanged={isMailingAddressChanged}
                                        isLicenseNameChanged={isLicenseNameChanged}
                                        isBusinessNameChanged={isBusinessNameChanged}
                                        data={data}
                                        setIsAddressChangedConfirmed={setIsAddressChangedConfirmed}
                                        setIsNameChangedConfirmed={setIsNameChangedConfirmed}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
        </ModalComponent >
    )
}

export default UploadLicenseModal
